import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { StatCell } from '@workspace/ui/components/stat-cell'
import { useAdminStartupEmailUpdatesQuery } from '../hooks/use-admin-api'

type Data = NonNullable<ReturnType<typeof useAdminStartupEmailUpdatesQuery>['data']>['data']
type Update = Data['updates'][number]
type Held = Data['held'][number]

const HELD_REASON: Record<string, string> = {
  NOT_A_FOUNDER: 'Sender is not a founder of this startup',
  NOT_AUTHENTICATED: 'Sender could not be authenticated for its own domain',
  EMPTY: 'Nothing left after removing quoted replies',
}

const formatTime = (value: Date | string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const stage = (status: string | null) => (status ? status.toLowerCase() : '-')

/** A startup's updates address, what its founders published by email, and the mail that was held. */
export function StartupEmailUpdatesTab({ startupId }: { startupId: string }) {
  const query = useAdminStartupEmailUpdatesQuery(startupId)
  const data = query.data?.data

  const updateColumns: TableColumn<Update>[] = [
    {
      header: 'Update',
      render: (u) => (
        <div className="space-y-0.5">
          <CellPrimary>{u.title}</CellPrimary>
          {u.removed && <Badge variant="outline">Removed by the founder</Badge>}
          <details className="pt-1">
            <summary className="cursor-pointer text-xs text-primary">
              Message and {u.facts.length} {u.facts.length === 1 ? 'fact' : 'facts'}
            </summary>
            <p className="mt-2 whitespace-pre-line text-sm text-foreground">{u.text}</p>
            <ul className="mt-3 space-y-2">
              {u.facts.map((f) => (
                <li key={f.claimId} className="text-xs text-muted-foreground">
                  <a
                    href={`/admin/startups/${startupId}?tab=facts&q=${encodeURIComponent(f.text.slice(0, 40))}`}
                    className="text-primary hover:underline"
                  >
                    {f.claimId.slice(0, 8)}
                  </a>{' '}
                  {f.text}
                  {f.kinds.length > 0 && ` · ${f.kinds.join(', ')}`}
                  {f.retired && ' · retired'}
                </li>
              ))}
            </ul>
          </details>
        </div>
      ),
    },
    {
      header: 'Received',
      width: '180px',
      render: (u) => <CellText>{formatTime(u.receivedAt)}</CellText>,
    },
    {
      header: 'Facts',
      width: '120px',
      render: (u) => (
        <CellText>
          {stage(u.factScanStatus)}
          {u.factCount !== null ? ` · ${u.factCount}` : ''}
        </CellText>
      ),
    },
    {
      header: 'Classified',
      width: '110px',
      render: (u) => <CellText>{stage(u.classifyStatus)}</CellText>,
    },
    {
      header: 'Alerts read',
      width: '110px',
      render: (u) => <CellText>{stage(u.watchdogStatus)}</CellText>,
    },
    {
      header: 'Alerts sent',
      width: '100px',
      render: (u) => <CellText>{u.alertCount}</CellText>,
    },
  ]

  const heldColumns: TableColumn<Held>[] = [
    {
      header: 'From',
      render: (h) => <CellPrimary>{h.fromAddress}</CellPrimary>,
    },
    {
      header: 'Subject',
      render: (h) => (
        <div className="space-y-1">
          <CellText>{h.subject ?? '-'}</CellText>
          {h.bodyText && (
            <details>
              <summary className="cursor-pointer text-xs text-primary">Message</summary>
              <p className="mt-2 whitespace-pre-line text-sm text-foreground">{h.bodyText}</p>
            </details>
          )}
        </div>
      ),
    },
    {
      header: 'Why it was held',
      render: (h) => <CellText>{HELD_REASON[h.reason] ?? h.reason}</CellText>,
    },
    {
      header: 'Received',
      width: '180px',
      render: (h) => <CellText>{formatTime(h.receivedAt)}</CellText>,
    },
  ]

  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Updates address</h2>
        {data?.address ? (
          <code className="inline-block rounded-lg border border-border bg-input/50 px-3 py-2 text-sm">
            {data.address}
          </code>
        ) : (
          <CellText>The founder has not asked for an updates address yet.</CellText>
        )}
        <CellText className="block text-xs">
          Mail is published only from a founder of this startup whose address the provider
          authenticated for its own domain:{' '}
          {data && data.founderSenders.length > 0 ? data.founderSenders.join(', ') : 'none'}.
        </CellText>
      </section>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCell size="sm" value={data?.updates.length ?? '-'} label="Published" />
        <StatCell
          size="sm"
          value={data?.updates.filter((u) => u.removed).length ?? '-'}
          label="Removed by the founder"
        />
        <StatCell size="sm" value={data?.held.length ?? '-'} label="Held" />
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Received updates</h2>
        <DataTable
          columns={updateColumns}
          data={data?.updates ?? []}
          isLoading={query.isLoading}
          emptyMessage="Nothing received by email yet."
          keyExtractor={(u) => u.versionId}
          minWidth="800px"
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Held mail</h2>
        <CellText className="block text-xs">
          Not published. A stranger's text is never kept; the rest is deleted after{' '}
          {data?.heldRetentionDays ?? 30} days.
        </CellText>
        <DataTable
          columns={heldColumns}
          data={data?.held ?? []}
          isLoading={query.isLoading}
          emptyMessage="No held mail."
          keyExtractor={(h) => h.id}
          minWidth="800px"
        />
      </section>
    </div>
  )
}
